import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
    Box,
    TextField,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Stack,
    Checkbox,
    FormControlLabel,
    Alert,
    Menu
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import PartitionIcon from '@mui/icons-material/ViewModule';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SettingsIcon from '@mui/icons-material/Settings';
import { useSelector } from 'react-redux';
import { fieldTypes } from '../../../config/fieldTypesConfig';
import { fieldSelector } from '../../../utils/fieldSelector';
import { theme } from '../../../theme';
import TEXT from './text';
import ConstrainIcon from '../../../Icons/Constrain/ConstrainIcon';

const EditDbGroup = () => {
    const language = useSelector(state => state.App_Settings.language);
    
    // Debug: Check if DragDropContext is available
    useEffect(() => {
        console.log('[EditDbGroup][Init] Component mounted');
        console.log('[EditDbGroup][Init] DragDropContext available:', typeof DragDropContext !== 'undefined');
        console.log('[EditDbGroup][Init] Droppable available:', typeof Droppable !== 'undefined');
        console.log('[EditDbGroup][Init] Draggable available:', typeof Draggable !== 'undefined');
    }, []);
    
    // DbGroup selection and editing state
    const [availableDbGroups, setAvailableDbGroups] = useState([]);
    const [selectedDbGroup, setSelectedDbGroup] = useState(null);
    const [dbGroupName, setDbGroupName] = useState('');
    const [fields, setFields] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [dragDisabled, setDragDisabled] = useState(false);
    
    // Log dragDisabled state changes
    useEffect(() => {
        console.log('[EditDbGroup][DragState] dragDisabled changed to:', dragDisabled);
    }, [dragDisabled]);
    
    // Log fields state changes
    useEffect(() => {
        console.log('[EditDbGroup][FieldsState] fields changed, count:', fields.length);
        console.log('[EditDbGroup][FieldsState] fields:', fields.map(f => ({ id: f.id, name: f.name, type: f.type })));
    }, [fields]);
    
    // Add column dialog state
    const [addColumnDialog, setAddColumnDialog] = useState(false);
    const [newFieldName, setNewFieldName] = useState('');
    const [newFieldType, setNewFieldType] = useState('');
    const [newFieldMandatory, setNewFieldMandatory] = useState(false);
    
    // Constraint dialog state
    const [constraintDialog, setConstraintDialog] = useState(false);
    const [selectedFieldForConstraint, setSelectedFieldForConstraint] = useState(null);
    const [constraintType, setConstraintType] = useState('');
    const [constraintValue, setConstraintValue] = useState('');
    
    // Partition dialog state
    const [partitionDialog, setPartitionDialog] = useState(false);
    const [selectedFieldForPartition, setSelectedFieldForPartition] = useState(null);
    const [partitionType, setPartitionType] = useState('');
    const [partitionValue, setPartitionValue] = useState('');
    
    // Options dialog state
    const [optionsDialog, setOptionsDialog] = useState(false);
    const [selectedFieldForOptions, setSelectedFieldForOptions] = useState(null);
    const [editedOptions, setEditedOptions] = useState({});
    
    // Menu state for field actions
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedFieldForMenu, setSelectedFieldForMenu] = useState(null);
    const openMenu = Boolean(anchorEl);
    
    // Load available DbGroups from IndexedDB
    useEffect(() => {
        loadDbGroups();
    }, []);
    
    const loadDbGroups = () => {
        // First, open without version to check current state
        const checkRequest = indexedDB.open('ShepherdDB');
        
        checkRequest.onsuccess = (event) => {
            const db = event.target.result;
            const currentVersion = db.version;
            const needsUpgrade = !db.objectStoreNames.contains('DBgroups') ||
                                 !db.objectStoreNames.contains('Databases');
            
            console.log('[EditDbGroup] Current DB version:', currentVersion, 'Needs upgrade:', needsUpgrade);
            
            // Check if DBgroups has the name index
            let needsIndex = false;
            if (db.objectStoreNames.contains('DBgroups')) {
                try {
                    const transaction = db.transaction(['DBgroups'], 'readonly');
                    const objectStore = transaction.objectStore('DBgroups');
                    needsIndex = !objectStore.indexNames.contains('name');
                } catch (e) {
                    console.warn('[EditDbGroup] Could not check for index:', e);
                }
            }
            
            db.close();
            
            let request;
            if (needsUpgrade || needsIndex) {
                // Need to upgrade, open with new version
                console.log('[EditDbGroup] Database needs upgrade, opening with version', currentVersion + 1);
                request = indexedDB.open('ShepherdDB', currentVersion + 1);
                
                request.onupgradeneeded = (event) => {
                    console.log('[EditDbGroup] Database upgrade in progress');
                    const db = event.target.result;
                    
                    // Create DBgroups object store if it doesn't exist
                    if (!db.objectStoreNames.contains('DBgroups')) {
                        const objectStore = db.createObjectStore('DBgroups', { keyPath: 'id', autoIncrement: true });
                        objectStore.createIndex('name', 'name', { unique: false });
                        console.log('[EditDbGroup] DBgroups object store created');
                    } else if (needsIndex) {
                        // Add index to existing store
                        const transaction = event.target.transaction;
                        const objectStore = transaction.objectStore('DBgroups');
                        if (!objectStore.indexNames.contains('name')) {
                            objectStore.createIndex('name', 'name', { unique: false });
                            console.log('[EditDbGroup] Added name index to existing DBgroups');
                        }
                    }
                    
                    // Create Databases object store if it doesn't exist
                    if (!db.objectStoreNames.contains('Databases')) {
                        const objectStore = db.createObjectStore('Databases', { keyPath: 'id', autoIncrement: true });
                        objectStore.createIndex('name', 'name', { unique: false });
                        objectStore.createIndex('dbGroupId', 'dbGroupId', { unique: false });
                        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
                        console.log('[EditDbGroup] Databases object store created');
                    }
                };
            } else {
                // Everything exists, open normally
                console.log('[EditDbGroup] Database ready, opening normally');
                request = indexedDB.open('ShepherdDB');
            }
            
            request.onsuccess = (event) => {
                console.log('[EditDbGroup] Database opened successfully');
                const db = event.target.result;
                
                // Verify the object store exists before attempting to access it
                if (!db.objectStoreNames.contains('DBgroups')) {
                    console.error('[EditDbGroup] DBgroups object store does not exist in database after opening');
                    setAvailableDbGroups([]);
                    db.close();
                    return;
                }
                
                try {
                    const transaction = db.transaction(['DBgroups'], 'readonly');
                    
                    transaction.onerror = (error) => {
                        console.error('[EditDbGroup] Transaction failed:', error);
                        setAvailableDbGroups([]);
                        db.close();
                    };
                    
                    const objectStore = transaction.objectStore('DBgroups');
                    const getAllRequest = objectStore.getAll();
                    
                    getAllRequest.onsuccess = () => {
                        const result = getAllRequest.result || [];
                        setAvailableDbGroups(result);
                        console.log('[EditDbGroup] Loaded DbGroups:', result.length, 'groups found');
                        if (result.length === 0) {
                            console.log('[EditDbGroup] No DbGroups found in database - this is normal for a new installation');
                        }
                    };
                    
                    getAllRequest.onerror = (error) => {
                        console.error('[EditDbGroup] Error retrieving DbGroups:', error);
                        setAvailableDbGroups([]);
                    };
                    
                    transaction.oncomplete = () => {
                        db.close();
                        console.log('[EditDbGroup] Transaction completed and database closed');
                    };
                } catch (error) {
                    console.error('[EditDbGroup] Exception during transaction:', error);
                    setAvailableDbGroups([]);
                    db.close();
                }
            };
            
            request.onerror = (error) => {
                console.error('[EditDbGroup] Error opening IndexedDB:', error);
                console.error('[EditDbGroup] Error event:', error);
                console.error('[EditDbGroup] Error target:', error.target);
                if (error.target?.error) {
                    console.error('[EditDbGroup] Error details:', error.target.error.name, error.target.error.message);
                }
                setAvailableDbGroups([]);
            };
        };
        
        checkRequest.onerror = (error) => {
            console.error('[EditDbGroup] Error checking IndexedDB:', error);
            console.error('[EditDbGroup] Error event:', error);
            if (error.target?.error) {
                console.error('[EditDbGroup] Error details:', error.target.error.name, error.target.error.message);
            }
            setAvailableDbGroups([]);
            
            // Try to create database with version 1 as fallback
            console.log('[EditDbGroup] Attempting to create database with version 1 as fallback');
            const fallbackRequest = indexedDB.open('ShepherdDB');
            
            fallbackRequest.onupgradeneeded = (event) => {
                console.log('[EditDbGroup] Creating new database');
                const db = event.target.result;
                
                if (!db.objectStoreNames.contains('DBgroups')) {
                    const objectStore = db.createObjectStore('DBgroups', { keyPath: 'id', autoIncrement: true });
                    objectStore.createIndex('name', 'name', { unique: false });
                    console.log('[EditDbGroup] DBgroups object store created');
                }
                
                if (!db.objectStoreNames.contains('Databases')) {
                    const objectStore = db.createObjectStore('Databases', { keyPath: 'id', autoIncrement: true });
                    objectStore.createIndex('name', 'name', { unique: false });
                    objectStore.createIndex('dbGroupId', 'dbGroupId', { unique: false });
                    objectStore.createIndex('timestamp', 'timestamp', { unique: false });
                    console.log('[EditDbGroup] Databases object store created');
                }
            };
            
            fallbackRequest.onsuccess = (event) => {
                console.log('[EditDbGroup] Fallback database created successfully');
                const db = event.target.result;
                db.close();
                // Retry loading
                loadDbGroups();
            };
            
            fallbackRequest.onerror = (error) => {
                console.error('[EditDbGroup] Fallback database creation also failed:', error);
                if (error.target?.error) {
                    console.error('[EditDbGroup] Fallback error details:', error.target.error.name, error.target.error.message);
                }
            };
        };
    };
    
    const get_item_by_id = (id) => {
        const item = fields.find(item => item.id === id);
        return item;
    };
    
    const get_item_index_by_id = (id) => {
        const index = fields.findIndex(item => item.id === id);
        return index;
    };
    
    const handleSelectDbGroup = (dbGroup) => {
        setSelectedDbGroup(dbGroup);
        setDbGroupName(dbGroup.name);
        
        // Parse fields, handle Custom Note values
        const parsedFields = dbGroup.fields.map(field => {
            if (field.type === 'Custom Note' && typeof field.value === 'string') {
                try {
                    return { ...field, value: JSON.parse(field.value) };
                } catch (e) {
                    return field;
                }
            }
            return field;
        });
        
        setFields(parsedFields);
        setHasChanges(false);
        console.log('[EditDbGroup] Selected DbGroup:', dbGroup);
    };
    
    // Add new column
    const handleAddColumn = () => {
        if (!newFieldName || !newFieldType) {
            alert('Please provide both field name and type');
            return;
        }
        
        const newField = {
            id: `field-${Date.now()}`,
            name: newFieldName,
            type: newFieldType,
            mandatory: newFieldMandatory,
            properties: {
                options: { required: newFieldMandatory, label: newFieldName },
                constrains: {},
                KeepItSimple: true
            },
            value: null,
            valid: false
        };
        
        setFields([...fields, newField]);
        setHasChanges(true);
        setAddColumnDialog(false);
        
        // Reset form
        setNewFieldName('');
        setNewFieldType('');
        setNewFieldMandatory(false);
        
        console.log('[EditDbGroup] Added new column:', newField);
    };
    
    // Handle drag and drop for reordering fields
    const handleDragEnd = (result) => {
        console.log('[EditDbGroup][DragEnd] === DRAG END CALLED ===');
        console.log('[EditDbGroup][DragEnd] Result:', JSON.stringify(result, null, 2));
        console.log('[EditDbGroup][DragEnd] dragDisabled state:', dragDisabled);
        console.log('[EditDbGroup][DragEnd] Current fields count:', fields.length);
        console.log('[EditDbGroup][DragEnd] Fields:', fields.map(f => ({ id: f.id, name: f.name, type: f.type })));
        
        if (!result.destination) {
            console.log('[EditDbGroup][DragEnd] ❌ No destination - drag cancelled');
            return;
        }
        
        console.log('[EditDbGroup][DragEnd] Source index:', result.source.index);
        console.log('[EditDbGroup][DragEnd] Destination index:', result.destination.index);
        console.log('[EditDbGroup][DragEnd] Draggable ID:', result.draggableId);
        console.log('[EditDbGroup][DragEnd] Source droppableId:', result.source.droppableId);
        console.log('[EditDbGroup][DragEnd] Destination droppableId:', result.destination.droppableId);
        
        console.log('[EditDbGroup] Dragging field:', result);
        
        // Check if the drag is happening within a Custom Note
        if (result.source.droppableId.startsWith('custom-note-droppable-')) {
            console.log('[EditDbGroup][CustomNote] Custom Note internal drag detected');
            console.log('[EditDbGroup][CustomNote] Source droppableId:', result.source.droppableId);
            
            // Find the Custom Note item by extracting the ID from droppableId
            const customNoteId = result.source.droppableId.replace('custom-note-droppable-', '');
            const customNoteItem = fields.find(item => item.id === customNoteId);
            
            if (customNoteItem && customNoteItem.value) {
                // Reorder the fields within the Custom Note
                const reorderedFields = Array.from(customNoteItem.value);
                const [removed] = reorderedFields.splice(result.source.index, 1);
                reorderedFields.splice(result.destination.index, 0, removed);
                
                // Update the fields array with the reordered fields
                const updatedFields = fields.map(item => 
                    item.id === customNoteId 
                        ? { ...item, value: reorderedFields }
                        : item
                );
                
                console.log('[EditDbGroup][CustomNote] ✅ Reordered Custom Note fields:', reorderedFields);
                console.log('[EditDbGroup][CustomNote] Updated fields:', updatedFields);
                setFields(updatedFields);
                setHasChanges(true);
                console.log('[EditDbGroup][CustomNote] State updated successfully');
            }
            return;
        }
        
        // Handle main fields drag
        console.log('[EditDbGroup][MainDrag] Handling main fields drag');
        console.log('[EditDbGroup][MainDrag] Before reorder:', fields.map(f => f.name));
        
        const reorderedFields = Array.from(fields);
        console.log('[EditDbGroup][MainDrag] Created array copy, length:', reorderedFields.length);
        
        const [removed] = reorderedFields.splice(result.source.index, 1);
        console.log('[EditDbGroup][MainDrag] Removed field:', removed?.name, 'from index:', result.source.index);
        
        reorderedFields.splice(result.destination.index, 0, removed);
        console.log('[EditDbGroup][MainDrag] Inserted at index:', result.destination.index);
        console.log('[EditDbGroup][MainDrag] After reorder:', reorderedFields.map(f => f.name));
        
        setFields(reorderedFields);
        setHasChanges(true);
        console.log('[EditDbGroup][MainDrag] ✅ State updated successfully');
    };
    
    // Remove column
    const handleRemoveColumn = (fieldId) => {
        const field = fields.find(f => f.id === fieldId);
        const confirmRemove = window.confirm(
            `Are you sure you want to remove the column "${field.name}"?\n\nThis action cannot be undone.`
        );
        
        if (!confirmRemove) return;
        
        const updatedFields = fields.filter(f => f.id !== fieldId);
        setFields(updatedFields);
        setHasChanges(true);
        
        console.log('[EditDbGroup] Removed column:', field.name);
    };
    
    // Add constraint to column
    const handleOpenConstraintDialog = (field) => {
        setSelectedFieldForConstraint(field);
        setConstraintType('');
        setConstraintValue('');
        setConstraintDialog(true);
    };
    
    const handleAddConstraint = () => {
        if (!constraintType || !constraintValue) {
            alert('Please provide both constraint type and value');
            return;
        }
        
        const updatedFields = fields.map(field => {
            if (field.id === selectedFieldForConstraint.id) {
                const updatedConstraints = {
                    ...field.properties.constrains,
                    [constraintType]: constraintValue
                };
                
                return {
                    ...field,
                    properties: {
                        ...field.properties,
                        constrains: updatedConstraints,
                        KeepItSimple: false
                    }
                };
            }
            return field;
        });
        
        setFields(updatedFields);
        setHasChanges(true);
        setConstraintDialog(false);
        
        console.log('[EditDbGroup] Added constraint:', constraintType, constraintValue);
    };
    
    // Add partition to column
    const handleOpenPartitionDialog = (field) => {
        setSelectedFieldForPartition(field);
        setPartitionType('');
        setPartitionValue('');
        setPartitionDialog(true);
    };
    
    const handleAddPartition = () => {
        if (!partitionType || !partitionValue) {
            alert('Please provide both partition type and value');
            return;
        }
        
        const updatedFields = fields.map(field => {
            if (field.id === selectedFieldForPartition.id) {
                const updatedOptions = {
                    ...field.properties.options,
                    partition: {
                        type: partitionType,
                        value: partitionValue
                    }
                };
                
                return {
                    ...field,
                    properties: {
                        ...field.properties,
                        options: updatedOptions
                    }
                };
            }
            return field;
        });
        
        setFields(updatedFields);
        setHasChanges(true);
        setPartitionDialog(false);
        
        console.log('[EditDbGroup] Added partition:', partitionType, partitionValue);
    };
    
    // Save changes to IndexedDB
    const handleSaveChanges = () => {
        if (!dbGroupName || dbGroupName.trim() === '') {
            alert('DbGroup name cannot be empty');
            return;
        }
        
        // Process Custom Note fields
        const processedFields = fields.map(field => {
            if (field.type === 'Custom Note' && field.value && typeof field.value !== 'string') {
                return {
                    ...field,
                    value: JSON.stringify(field.value)
                };
            }
            return field;
        });
        
        const updatedDbGroup = {
            ...selectedDbGroup,
            name: dbGroupName.trim(),
            timestamp: new Date().toISOString(),
            totalFields: processedFields.length,
            fields: processedFields,
            version: (selectedDbGroup.version || 1) + 1
        };
        
        // Update in IndexedDB - open without version to use current version
        const request = indexedDB.open('ShepherdDB');
        
        request.onsuccess = (event) => {
            const db = event.target.result;
            
            // Verify the object store exists before attempting to access it
            if (!db.objectStoreNames.contains('DBgroups')) {
                console.error('[EditDbGroup] DBgroups object store does not exist in database');
                alert('❌ Database schema error. Please try again or refresh the page.');
                db.close();
                return;
            }
            
            const transaction = db.transaction(['DBgroups'], 'readwrite');
            
            transaction.onerror = (error) => {
                console.error('[EditDbGroup] Transaction failed during save:', error);
                alert('❌ Error updating DbGroup. Please try again.');
            };
            
            const objectStore = transaction.objectStore('DBgroups');
            const updateRequest = objectStore.put(updatedDbGroup);
            
            updateRequest.onsuccess = () => {
                console.log('[EditDbGroup] DbGroup updated successfully');
                alert(`✅ DbGroup "${dbGroupName}" updated successfully!\n\nVersion: ${updatedDbGroup.version}\nTotal fields: ${processedFields.length}`);
                
                setHasChanges(false);
                loadDbGroups();
            };
            
            updateRequest.onerror = (error) => {
                console.error('[EditDbGroup] Error updating DbGroup:', error);
                alert('❌ Error updating DbGroup. Please try again.');
            };
            
            transaction.oncomplete = () => {
                db.close();
            };
        };
        
        request.onerror = (error) => {
            console.error('[EditDbGroup] Error opening IndexedDB:', error);
            alert('❌ Error accessing IndexedDB. Please try again.');
        };
    };
    
    // Get available constraint types for a field
    const getAvailableConstraints = (fieldType) => {
        const type = fieldTypes.find(t => t.type === fieldType);
        return type?.constrains || {};
    };
    
    // Get available options for a field type
    const getAvailableOptions = (fieldType) => {
        const type = fieldTypes.find(t => t.type === fieldType);
        return type?.options || {};
    };
    
    // Handle menu open/close
    const handleMenuClick = (event, field) => {
        setAnchorEl(event.currentTarget);
        setSelectedFieldForMenu(field);
    };
    
    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedFieldForMenu(null);
    };
    
    // Open options editor dialog
    const handleOpenOptionsDialog = (field) => {
        setSelectedFieldForOptions(field);
        // Initialize with current options or default values from fieldTypesConfig
        const defaultOptions = getAvailableOptions(field.type);
        const currentOptions = field.properties?.options || {};
        
        // Merge default options with current options
        const mergedOptions = { ...defaultOptions, ...currentOptions };
        setEditedOptions(mergedOptions);
        setOptionsDialog(true);
    };
    
    // Handle option value change
    const handleOptionChange = (optionKey, value) => {
        setEditedOptions(prev => ({
            ...prev,
            [optionKey]: value
        }));
    };
    
    // Save edited options
    const handleSaveOptions = () => {
        const updatedFields = fields.map(field => {
            if (field.id === selectedFieldForOptions.id) {
                return {
                    ...field,
                    properties: {
                        ...field.properties,
                        options: editedOptions
                    }
                };
            }
            return field;
        });
        
        setFields(updatedFields);
        setHasChanges(true);
        setOptionsDialog(false);
        
        console.log('[EditDbGroup] Updated field options:', editedOptions);
    };
    
    // Render input for option based on its type
    const renderOptionInput = (optionKey, optionValue) => {
        const inputType = typeof optionValue;
        
        // Special handling for Autocomplete field type
        const isAutocompleteField = selectedFieldForOptions?.type === 'Autocomplete';
        
        // Check if smart autocomplete or manual config is active
        const isSmartAutocompleteActive = isAutocompleteField && editedOptions.smartAutocomplete !== false;
        const isManualConfigActive = isAutocompleteField && (
            editedOptions.useDatabase || 
            editedOptions.databaseName || 
            editedOptions.dbGroupName || 
            editedOptions.columnName
        );
        
        // Handle smartAutocomplete option
        if (optionKey === 'smartAutocomplete' && isAutocompleteField) {
            return (
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={editedOptions[optionKey] !== false}
                                onChange={(e) => {
                                    handleOptionChange(optionKey, e.target.checked);
                                    // If enabling smart autocomplete, clear manual config
                                    if (e.target.checked) {
                                        handleOptionChange('useDatabase', false);
                                        handleOptionChange('databaseName', '');
                                        handleOptionChange('dbGroupName', '');
                                        handleOptionChange('columnName', '');
                                    }
                                }}
                                disabled={isManualConfigActive}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body2">
                                    {optionKey} (Recommended)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Automatically detects database and uses field position as column
                                </Typography>
                            </Box>
                        }
                    />
                    {isManualConfigActive && (
                        <Typography variant="caption" color="warning.main" display="block" sx={{ ml: 4, mt: 0.5 }}>
                            ⚠️ Disabled when manual database configuration is active
                        </Typography>
                    )}
                </Box>
            );
        }
        
        // Disable manual database config options when smart autocomplete is active
        const isDisabled = isAutocompleteField && isSmartAutocompleteActive && (
            optionKey === 'useDatabase' || 
            optionKey === 'databaseName' || 
            optionKey === 'dbGroupName' || 
            optionKey === 'columnName'
        );
        
        if (inputType === 'boolean') {
            return (
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={editedOptions[optionKey] || false}
                                onChange={(e) => {
                                    handleOptionChange(optionKey, e.target.checked);
                                    // If enabling manual config, disable smart autocomplete
                                    if (optionKey === 'useDatabase' && e.target.checked && isAutocompleteField) {
                                        handleOptionChange('smartAutocomplete', false);
                                    }
                                }}
                                disabled={isDisabled}
                            />
                        }
                        label={optionKey}
                    />
                    {isDisabled && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: 0.5 }}>
                            Disabled when Smart Autocomplete is active
                        </Typography>
                    )}
                </Box>
            );
        }
        
        if (inputType === 'number') {
            return (
                <TextField
                    fullWidth
                    type="number"
                    label={optionKey}
                    value={editedOptions[optionKey] || 0}
                    onChange={(e) => handleOptionChange(optionKey, parseFloat(e.target.value) || 0)}
                    helperText={`Type: Number`}
                    disabled={isDisabled}
                />
            );
        }
        
        if (Array.isArray(optionValue)) {
            return (
                <TextField
                    fullWidth
                    label={optionKey}
                    value={Array.isArray(editedOptions[optionKey]) ? editedOptions[optionKey].join(', ') : ''}
                    onChange={(e) => {
                        const arrayValue = e.target.value.split(',').map(item => item.trim()).filter(item => item);
                        handleOptionChange(optionKey, arrayValue);
                    }}
                    helperText="Comma-separated values (e.g., Option 1, Option 2, Option 3)"
                    multiline
                    rows={2}
                    disabled={isDisabled}
                />
            );
        }
        
        // Default to text field for strings and other types
        return (
            <TextField
                fullWidth
                label={optionKey}
                value={editedOptions[optionKey] || ''}
                onChange={(e) => {
                    handleOptionChange(optionKey, e.target.value);
                    // If setting manual config fields, disable smart autocomplete
                    if ((optionKey === 'databaseName' || optionKey === 'dbGroupName' || optionKey === 'columnName') 
                        && e.target.value && isAutocompleteField) {
                        handleOptionChange('smartAutocomplete', false);
                        handleOptionChange('useDatabase', true);
                    }
                }}
                helperText={isDisabled ? 'Disabled when Smart Autocomplete is active' : `Type: ${inputType}`}
                disabled={isDisabled}
            />
        );
    };
    
    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                {TEXT.EditDbGroupTitle?.[language] || 'Edit DbGroup'}
            </Typography>
            
            {/* DbGroup Selection */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {TEXT.SelectDbGroup?.[language] || 'Select DbGroup to Edit'}
                    </Typography>
                    
                    {availableDbGroups.length === 0 ? (
                        <Alert severity="warning">
                            {TEXT.NoDbGroupsAvailable?.[language] || 'No DbGroups available. Please create a DbGroup first.'}
                        </Alert>
                    ) : (
                        <FormControl fullWidth>
                            <InputLabel>{TEXT.DbGroup?.[language] || 'DbGroup'}</InputLabel>
                            <Select
                                value={selectedDbGroup?.id || ''}
                                onChange={(e) => {
                                    const dbGroup = availableDbGroups.find(dg => dg.id === e.target.value);
                                    handleSelectDbGroup(dbGroup);
                                }}
                                label={TEXT.DbGroup?.[language] || 'DbGroup'}
                            >
                                {availableDbGroups.map((dbGroup) => (
                                    <MenuItem key={dbGroup.id} value={dbGroup.id}>
                                        {dbGroup.name} ({dbGroup.totalFields} fields, v{dbGroup.version || 1})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </CardContent>
            </Card>
            
            {/* Edit Section */}
            {selectedDbGroup && (
                <>
                    {/* DbGroup Name */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <TextField
                                fullWidth
                                label={TEXT.DbGroupName?.[language] || 'DbGroup Name'}
                                value={dbGroupName}
                                onChange={(e) => {
                                    setDbGroupName(e.target.value);
                                    setHasChanges(true);
                                }}
                                InputProps={{
                                    style: { backgroundColor: theme.palette.background.main }
                                }}
                            />
                        </CardContent>
                    </Card>
                    
                    {/* Action Buttons */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setAddColumnDialog(true)}
                                >
                                    {TEXT.AddColumn?.[language] || 'Add Column'}
                                </Button>
                                
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSaveChanges}
                                    disabled={!hasChanges}
                                >
                                    {TEXT.SaveChanges?.[language] || 'Save Changes'}
                                </Button>
                                
                                {hasChanges && (
                                    <Chip 
                                        label={TEXT.UnsavedChanges?.[language] || 'Unsaved Changes'}
                                        color="warning"
                                        size="small"
                                    />
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                    
                    {/* Fields Preview with Drag & Drop */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                        {TEXT.Columns?.[language] || 'Columns'} ({fields.length})
                    </Typography>
                    
                    <Box sx={{ 
                        mt: 2, 
                        p: 2, 
                        border: '1px solid #ccc', 
                        borderRadius: '4px',
                        backgroundColor: theme.palette.background.paper
                    }}>
                        <DragDropContext 
                            onDragStart={(start) => {
                                console.log('[EditDbGroup][DragStart] === DRAG STARTED ===');
                                console.log('[EditDbGroup][DragStart] Dragging:', start.draggableId);
                                console.log('[EditDbGroup][DragStart] From index:', start.source.index);
                                console.log('[EditDbGroup][DragStart] DroppableId:', start.source.droppableId);
                                console.log('[EditDbGroup][DragStart] dragDisabled:', dragDisabled);
                            }}
                            onDragUpdate={(update) => {
                                console.log('[EditDbGroup][DragUpdate] Dragging over index:', update.destination?.index);
                            }}
                            onDragEnd={handleDragEnd}
                        >
                            <Droppable droppableId="droppable" key={fields.length}>
                                {(provided, snapshot) => {
                                    console.log('[EditDbGroup][Droppable] Rendering, isDraggingOver:', snapshot.isDraggingOver);
                                    return (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        style={{
                                            listStyleType: 'none',
                                            padding: 0,
                                            minHeight: '50px',
                                        }}
                                    >
                                        {fields.map((field, index) => {
                                            // Ensure unique draggableId with prefix
                                            const draggableId = `field-${field.id}`;
                                            console.log('[EditDbGroup][Draggable] Rendering field:', field.name, 'at index:', index);
                                            console.log('[EditDbGroup][Draggable] - field.id:', field.id);
                                            console.log('[EditDbGroup][Draggable] - draggableId:', draggableId);
                                            console.log('[EditDbGroup][Draggable] - isDragDisabled:', dragDisabled);
                                            
                                            if (dragDisabled) {
                                                console.warn('[EditDbGroup][Draggable] ⚠️ DRAG IS DISABLED for field:', field.name);
                                            }
                                            return (
                                            <Draggable
                                                key={field.id}
                                                draggableId={String(field.id)}
                                                index={index}
                                                isDragDisabled={dragDisabled}
                                            >
                                                {(provided, snapshot) => {
                                                    console.log('[EditDbGroup][Draggable] Render - isDragging:', snapshot.isDragging, 'isDragDisabled:', dragDisabled);
                                                    
                                                    if (snapshot.isDragging) {
                                                        console.log('[EditDbGroup][Draggable] ✅ Field is being dragged:', field.name);
                                                    }
                                                    
                                                    return (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={{
                                                            padding: '12px',
                                                            margin: '8px 0',
                                                            backgroundColor: '#eeeeeeff',
                                                            borderRadius: '4px',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            gap: '10px',
                                                            ...provided.draggableProps.style,
                                                        }}
                                                    >
                                                        {/* Drag Handle */}
                                                        <Box 
                                                            sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                mr: 1, 
                                                                color: 'text.secondary', 
                                                                cursor: 'grab'
                                                            }}
                                                        >
                                                            <DragIndicatorIcon />
                                                        </Box>
                                                        
                                                        {/* Field Preview */}
                                                        <Box sx={{ flex: 1, pointerEvents: 'none' }}>
                                                            {fieldSelector(
                                                                field, 
                                                                fields, 
                                                                setFields, 
                                                                get_item_by_id, 
                                                                get_item_index_by_id, 
                                                                setDragDisabled,
                                                                'design'
                                                            )}
                                                            
                                                            {/* Show constraints */}
                                                            {field.properties?.constrains && Object.keys(field.properties.constrains).length > 0 && (
                                                                <Box sx={{ mt: 1 }}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Constraints:
                                                                    </Typography>
                                                                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                                                        {Object.entries(field.properties.constrains).map(([key, value]) => (
                                                                            <Chip
                                                                                key={key}
                                                                                label={`${key}: ${value}`}
                                                                                size="small"
                                                                                variant="outlined"
                                                                            />
                                                                        ))}
                                                                    </Stack>
                                                                </Box>
                                                            )}
                                                            
                                                            {/* Show partition */}
                                                            {field.properties?.options?.partition && (
                                                                <Box sx={{ mt: 1 }}>
                                                                    <Chip
                                                                        icon={<PartitionIcon />}
                                                                        label={`Partition: ${field.properties.options.partition.type} - ${field.properties.options.partition.value}`}
                                                                        size="small"
                                                                        color="primary"
                                                                        variant="outlined"
                                                                    />
                                                                </Box>
                                                            )}
                                                        </Box>
                                                        
                                                        {/* Action Menu */}
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleMenuClick(e, field)}
                                                            sx={{ flexShrink: 0 }}
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    </div>
                                                    );
                                                }}
                                            </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                    );
                                }}
                            </Droppable>
                        </DragDropContext>
                    </Box>
                </>
            )}
            
            {/* Add Column Dialog */}
            <Dialog open={addColumnDialog} onClose={() => setAddColumnDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{TEXT.AddNewColumn?.[language] || 'Add New Column'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label={TEXT.ColumnName?.[language] || 'Column Name'}
                            value={newFieldName}
                            onChange={(e) => setNewFieldName(e.target.value)}
                        />
                        
                        <FormControl fullWidth>
                            <InputLabel>{TEXT.ColumnType?.[language] || 'Column Type'}</InputLabel>
                            <Select
                                value={newFieldType}
                                onChange={(e) => setNewFieldType(e.target.value)}
                                label={TEXT.ColumnType?.[language] || 'Column Type'}
                            >
                                {fieldTypes.map((type) => (
                                    <MenuItem key={type.type} value={type.type}>
                                        {type.type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={newFieldMandatory}
                                    onChange={(e) => setNewFieldMandatory(e.target.checked)}
                                />
                            }
                            label={TEXT.MandatoryField?.[language] || 'Mandatory Field'}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddColumnDialog(false)}>
                        {TEXT.Cancel?.[language] || 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddColumn}
                        disabled={!newFieldName || !newFieldType}
                    >
                        {TEXT.Add?.[language] || 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Add Constraint Dialog */}
            <Dialog open={constraintDialog} onClose={() => setConstraintDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {TEXT.AddConstraint?.[language] || 'Add Constraint'} - {selectedFieldForConstraint?.name}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>{TEXT.ConstraintType?.[language] || 'Constraint Type'}</InputLabel>
                            <Select
                                value={constraintType}
                                onChange={(e) => setConstraintType(e.target.value)}
                                label={TEXT.ConstraintType?.[language] || 'Constraint Type'}
                            >
                                {selectedFieldForConstraint && 
                                    Object.entries(getAvailableConstraints(selectedFieldForConstraint.type)).map(([key, value]) => (
                                        <MenuItem key={key} value={key}>
                                            {key} - {value.hint || value}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                        
                        <TextField
                            fullWidth
                            label={TEXT.ConstraintValue?.[language] || 'Constraint Value'}
                            value={constraintValue}
                            onChange={(e) => setConstraintValue(e.target.value)}
                            helperText={TEXT.ConstraintValueHint?.[language] || 'Enter the constraint value or expression'}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConstraintDialog(false)}>
                        {TEXT.Cancel?.[language] || 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddConstraint}
                        disabled={!constraintType || !constraintValue}
                    >
                        {TEXT.Add?.[language] || 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Add Partition Dialog */}
            <Dialog open={partitionDialog} onClose={() => setPartitionDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {TEXT.AddPartition?.[language] || 'Add Partition'} - {selectedFieldForPartition?.name}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>{TEXT.PartitionType?.[language] || 'Partition Type'}</InputLabel>
                            <Select
                                value={partitionType}
                                onChange={(e) => setPartitionType(e.target.value)}
                                label={TEXT.PartitionType?.[language] || 'Partition Type'}
                            >
                                <MenuItem value="range">Range</MenuItem>
                                <MenuItem value="list">List</MenuItem>
                                <MenuItem value="hash">Hash</MenuItem>
                                <MenuItem value="key">Key</MenuItem>
                            </Select>
                        </FormControl>
                        
                        <TextField
                            fullWidth
                            label={TEXT.PartitionValue?.[language] || 'Partition Value'}
                            value={partitionValue}
                            onChange={(e) => setPartitionValue(e.target.value)}
                            helperText={TEXT.PartitionValueHint?.[language] || 'Enter partition value or configuration'}
                            multiline
                            rows={2}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPartitionDialog(false)}>
                        {TEXT.Cancel?.[language] || 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddPartition}
                        disabled={!partitionType || !partitionValue}
                    >
                        {TEXT.Add?.[language] || 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Field Actions Dropdown Menu */}
            <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem 
                    onClick={() => {
                        handleOpenOptionsDialog(selectedFieldForMenu);
                        handleMenuClose();
                    }}
                >
                    <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                    {TEXT.EditOptions?.[language] || 'Options'}
                </MenuItem>
                <MenuItem 
                    onClick={() => {
                        handleOpenConstraintDialog(selectedFieldForMenu);
                        handleMenuClose();
                    }}
                >
                    <ConstrainIcon sx={{ fontSize: 20, mr: 1 }} />
                    {TEXT.AddConstraint?.[language] || 'Add Constraint'}
                </MenuItem>
                <MenuItem 
                    onClick={() => {
                        handleOpenPartitionDialog(selectedFieldForMenu);
                        handleMenuClose();
                    }}
                >
                    <PartitionIcon fontSize="small" sx={{ mr: 1 }} />
                    {TEXT.Partition?.[language] || 'Partition'}
                </MenuItem>
                <MenuItem 
                    onClick={() => {
                        handleRemoveColumn(selectedFieldForMenu.id);
                        handleMenuClose();
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    {TEXT.DeleteColumn?.[language] || 'Delete Column'}
                </MenuItem>
            </Menu>
            
            {/* Edit Options Dialog */}
            <Dialog 
                open={optionsDialog} 
                onClose={() => setOptionsDialog(false)} 
                maxWidth="md" 
                fullWidth
                PaperProps={{
                    sx: { maxHeight: '80vh' }
                }}
            >
                <DialogTitle>
                    {TEXT.EditFieldOptions?.[language] || 'Edit Field Options'} - {selectedFieldForOptions?.name}
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                        Field Type: {selectedFieldForOptions?.type}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        {/* Basic Field Properties */}
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                {TEXT.BasicProperties?.[language] || 'Basic Properties'}
                            </Typography>
                            <Stack spacing={2}>
                                <TextField
                                    fullWidth
                                    label={TEXT.FieldName?.[language] || 'Field Name'}
                                    value={selectedFieldForOptions?.name || ''}
                                    onChange={(e) => {
                                        const updatedFields = fields.map(field => 
                                            field.id === selectedFieldForOptions.id 
                                                ? { ...field, name: e.target.value }
                                                : field
                                        );
                                        setFields(updatedFields);
                                        setSelectedFieldForOptions({ ...selectedFieldForOptions, name: e.target.value });
                                        setHasChanges(true);
                                    }}
                                />
                                
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedFieldForOptions?.mandatory || false}
                                            onChange={(e) => {
                                                const updatedFields = fields.map(field => 
                                                    field.id === selectedFieldForOptions.id 
                                                        ? { 
                                                            ...field, 
                                                            mandatory: e.target.checked,
                                                            properties: {
                                                                ...field.properties,
                                                                options: {
                                                                    ...field.properties.options,
                                                                    required: e.target.checked
                                                                }
                                                            }
                                                        }
                                                        : field
                                                );
                                                setFields(updatedFields);
                                                setSelectedFieldForOptions({ ...selectedFieldForOptions, mandatory: e.target.checked });
                                                setHasChanges(true);
                                            }}
                                        />
                                    }
                                    label={TEXT.MandatoryField?.[language] || 'Mandatory Field'}
                                />
                            </Stack>
                        </Box>
                        
                        {/* Field Type Specific Options */}
                        {Object.keys(getAvailableOptions(selectedFieldForOptions?.type || '')).length > 0 && (
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    {TEXT.FieldTypeOptions?.[language] || 'Field Type Options'}
                                </Typography>
                                <Stack spacing={2}>
                                    {Object.entries(getAvailableOptions(selectedFieldForOptions?.type || '')).map(([key, value]) => (
                                        <Box key={key}>
                                            {renderOptionInput(key, value)}
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                        
                        {/* Additional Options (label, placeholder, etc.) */}
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                {TEXT.AdditionalOptions?.[language] || 'Additional Options'}
                            </Typography>
                            <Stack spacing={2}>
                                <TextField
                                    fullWidth
                                    label={TEXT.Label?.[language] || 'Label'}
                                    value={editedOptions.label || selectedFieldForOptions?.name || ''}
                                    onChange={(e) => handleOptionChange('label', e.target.value)}
                                    helperText="Display label for the field"
                                />
                                
                                <TextField
                                    fullWidth
                                    label={TEXT.Placeholder?.[language] || 'Placeholder'}
                                    value={editedOptions.placeholder || ''}
                                    onChange={(e) => handleOptionChange('placeholder', e.target.value)}
                                    helperText="Placeholder text shown when field is empty"
                                />
                                
                                <TextField
                                    fullWidth
                                    label={TEXT.HelperText?.[language] || 'Helper Text'}
                                    value={editedOptions.helperText || ''}
                                    onChange={(e) => handleOptionChange('helperText', e.target.value)}
                                    helperText="Help text shown below the field"
                                />
                                
                                <FormControl fullWidth>
                                    <InputLabel>{TEXT.Variant?.[language] || 'Variant'}</InputLabel>
                                    <Select
                                        value={editedOptions.variant || 'outlined'}
                                        onChange={(e) => handleOptionChange('variant', e.target.value)}
                                        label={TEXT.Variant?.[language] || 'Variant'}
                                    >
                                        <MenuItem value="outlined">Outlined</MenuItem>
                                        <MenuItem value="filled">Filled</MenuItem>
                                        <MenuItem value="standard">Standard</MenuItem>
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Box>
                        
                        {/* Show current options as JSON (for debugging/advanced users) */}
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {TEXT.CurrentConfiguration?.[language] || 'Current Configuration (JSON)'}
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                value={JSON.stringify(editedOptions, null, 2)}
                                InputProps={{
                                    readOnly: true,
                                    style: { fontFamily: 'monospace', fontSize: '0.875rem' }
                                }}
                            />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOptionsDialog(false)}>
                        {TEXT.Cancel?.[language] || 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveOptions}
                    >
                        {TEXT.SaveOptions?.[language] || 'Save Options'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EditDbGroup;
